import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Panel, PageHead, Stat, StatusDot } from "@/components/kit";
import { Activity, Cpu, Layers, Server } from "lucide-react";

const fmtAgo = (iso) => {
    if (!iso) return "sin datos";
    const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `hace ${s}s`;
    if (s < 3600) return `hace ${Math.floor(s / 60)}m`;
    return `hace ${Math.floor(s / 3600)}h`;
};

const QueueChips = ({ obj }) => (
    <div className="flex flex-wrap gap-1.5">
        {Object.entries(obj || {}).map(([k, v]) => (
            <span
                key={k}
                className="border border-border px-2 py-0.5 font-mono text-[11px] uppercase text-muted-foreground"
            >
                {k}: <span className="text-foreground">{v}</span>
            </span>
        ))}
        {!Object.keys(obj || {}).length && <span className="text-[11px] text-muted-foreground">vacío</span>}
    </div>
);

export default function System() {
    const { data } = useQuery({
        queryKey: ["system-health"],
        queryFn: async () => (await api.get("/system/health")).data,
        refetchInterval: 15000,
    });
    const workers = data?.workers || [];
    const q = data?.queues || {};
    const t = data?.totals || {};

    return (
        <div data-testid="system-page">
            <PageHead
                title="SALUD DEL SISTEMA"
                sub="Estado en vivo de los trabajadores autónomos, las colas de trabajo y los motores de video. Se refresca solo cada 15s."
            />

            <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
                <Stat
                    label="Trabajadores activos"
                    value={`${data?.workers_alive ?? "—"}/${data?.workers_total ?? "—"}`}
                    accent
                    testId="stat-workers"
                />
                <Stat label="Canales" value={t.channels ?? "—"} testId="stat-channels" />
                <Stat label="Videos publicados" value={t.videos_published ?? "—"} testId="stat-published" />
                <Stat label="A/B en curso" value={q.ab_tests_running ?? 0} testId="stat-ab" />
            </div>

            <Panel title="Trabajadores autónomos" right={<Server size={15} className="text-primary" />} testId="workers-panel">
                <div className="grid gap-2 md:grid-cols-2">
                    {workers.map((w) => (
                        <div
                            key={w.name}
                            className="flex items-center justify-between border border-border/60 px-3 py-2"
                            data-testid={`worker-${w.name}`}
                        >
                            <div className="flex items-center gap-2.5">
                                <StatusDot color={w.alive ? "#39FF14" : "#FF003C"} pulse={w.alive} />
                                <div>
                                    <div className="text-sm">{w.label}</div>
                                    <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                                        {w.schedule} · {fmtAgo(w.last_beat)}
                                    </div>
                                </div>
                            </div>
                            <span
                                className="font-mono text-[10px] uppercase"
                                style={{ color: w.alive ? "#39FF14" : "#FF003C" }}
                            >
                                {w.alive ? "vivo" : "inactivo"}
                            </span>
                        </div>
                    ))}
                    {!workers.length && <p className="text-sm text-muted-foreground">Cargando estado…</p>}
                </div>
            </Panel>

            <div className="mt-6 grid gap-6 md:grid-cols-2">
                <Panel title="Colas de trabajo" right={<Layers size={15} className="text-primary" />} testId="queues-panel">
                    <div className="space-y-3 text-xs">
                        <div>
                            <div className="label mb-1">Jobs (producción)</div>
                            <QueueChips obj={q.jobs} />
                        </div>
                        <div>
                            <div className="label mb-1">Publicaciones</div>
                            <QueueChips obj={q.publications} />
                        </div>
                        <div className="flex gap-4 pt-1">
                            <span className="text-muted-foreground">
                                Aprobaciones pendientes: <span className="text-foreground">{q.approvals_pending ?? 0}</span>
                            </span>
                        </div>
                    </div>
                </Panel>

                <Panel title="Motores de video (tu cuenta)" right={<Cpu size={15} className="text-primary" />} testId="engines-panel">
                    <div className="space-y-2 text-xs">
                        {(data?.engines || []).map((e) => (
                            <div key={e.engine} className="flex items-center justify-between border-b border-border/40 pb-1.5">
                                <span className="flex items-center gap-2">
                                    <StatusDot color={e.status === "ok" ? "#39FF14" : e.status === "unknown" ? "#666" : "#F5A623"} />
                                    <span className="uppercase">{e.engine}</span>
                                </span>
                                <span className="text-muted-foreground" title={e.detail}>
                                    {e.status} · {fmtAgo(e.at)}
                                </span>
                            </div>
                        ))}
                        {!(data?.engines || []).length && (
                            <p className="text-muted-foreground">
                                Sin intentos de render aún. El estado se llena cuando produces videos.
                            </p>
                        )}
                    </div>
                </Panel>
            </div>

            <div className="mt-6 flex items-center gap-2 text-[11px] text-muted-foreground">
                <Activity size={12} className="text-primary" /> Última comprobación: {fmtAgo(data?.checked_at)}
            </div>
        </div>
    );
}
